/**
 * The department a record belongs to is copied onto it (denormalised, for display and filtering)
 * when it is created and never rewritten afterwards, so a later department move of the employee
 * does not silently re-file their history. Every schema that carries these fields keeps them
 * together, last, in this order. Country-level separation comes from the organization, not a field.
 */
export const SCOPE_FIELDS = ['DepartmentId', 'DepartmentName', 'DepartmentKey'] as const;

export type ScopeField = (typeof SCOPE_FIELDS)[number];

export type ScopeStamp = Partial<Record<ScopeField, string>>;

type ScopeSource = Partial<Record<ScopeField, string | null | undefined>>;

/** The scope fields of `source` that actually have a value, ready to spread into an insert input. */
export const pickScopeStamp = (source?: ScopeSource | null): ScopeStamp => {
  const stamp: ScopeStamp = {};
  for (const field of SCOPE_FIELDS) {
    const value = source?.[field];
    if (value) stamp[field] = value;
  }
  return stamp;
};

/** `input` without the scope fields: every update goes through this so an edit can never change them. */
export const omitScopeFields = <T extends object>(input: T): Omit<T, ScopeField> => {
  const rest: Record<string, unknown> = { ...(input as Record<string, unknown>) };
  for (const field of SCOPE_FIELDS) delete rest[field];
  return rest as Omit<T, ScopeField>;
};
